import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  CircularProgress
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { APIHOST } from '../../../common';
import { Conference } from '../../../types/conference';
import { useApp } from '../../../components/AppContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import { getToken } from '../../../components/auth/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface OrderReportProps {
  conferences: Conference[];
}

interface Order {
  documentId: string;
  createdAt: string;
  orderStatus: string;
  confirmation: string;
  requiresBusParking: boolean;
  requiresTransitToVenue: boolean;
  order_rooms: {
    documentId: string;
    hotel: {
      documentId: string;
      name: string;
    };
    order_room_occupants: {
      documentId: string;
    }[];
  }[];
}

interface TransportationTotals {
  hotelName: string;
  busParking: number;
  transitToVenue: number;
}

export default function OrderReport({ conferences }: OrderReportProps) {
  const [selectedConference, setSelectedConference] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [transportationData, setTransportationData] = useState<TransportationTotals[]>([]);
  const [loading, setLoading] = useState(false);
  const { setSnackbarInput } = useApp();

  const handleConferenceChange = (event: SelectChangeEvent) => {
    const conferenceId = event.target.value;
    setSelectedConference(conferenceId);
    
    if (conferenceId) {
      setLoading(true);
      fetchOrders(conferenceId);
    }
  };

  const fetchOrders = async (conferenceId: string) => {
    try {
        const token = getToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

      
        const ordersResponse = await fetch(
            `${APIHOST}/api/orders?filters[conference_group][conference][documentId][$eq]=${conferenceId}&filters[orderStatus][$eq]=received&sort=createdAt:asc&populate[]=order_rooms.order_room_occupants&populate[]=conference_hotel.hotel&pagination[pageSize]=1000&pagination[page]=1`,
            {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            }
        )

        if (!ordersResponse.ok) {
          throw new Error('Failed to fetch orders');
        }

        const allOrders = await ordersResponse.json();
        setOrders(allOrders.data);
        
        // Process transportation data from the same orders
        // const transportationTotals = processTransportationData(allOrders.data);
        // setTransportationData(transportationTotals);
        
        setSnackbarInput({message: `Fetched ${allOrders.length} orders successfully`});
       
      } catch (error) {
        console.error('Error fetching orders:', error);
        setSnackbarInput({message: 'Error fetching orders', severity: 'error'});
        return [];
      } finally {
          setLoading(false);
      }

      
   

  };

//   const processTransportationData = (orders: Order[]): TransportationTotals[] => {
//     const hotelTotals: { [key: string]: TransportationTotals } = {};

//     orders.map(order => {
//       order.order_rooms.map(room => {
//         const hotelName = order.conference_hotel.hotel.longName;
//         const hotelId = order.conference_hotel.hotel.documentId;
//         const occupantsCount = room.order_room_occupants.length;
        
//         if (!hotelTotals[hotelId]) {
//           hotelTotals[hotelId] = {
//             hotelName,
//             busParking: 0,
//             transitToVenue: 0
//           };
//         }

//         if (order.requiresBusParking) {
//           hotelTotals[hotelId].busParking += occupantsCount;
//         }

//         if (order.requiresTransitToVenue) {
//           hotelTotals[hotelId].transitToVenue += occupantsCount;
//         }
//       });
//     });

//     return Object.values(hotelTotals).sort((a, b) => a.hotelName.localeCompare(b.hotelName));
//   };

  const processOrdersPerDay = () => {
    if (orders.length === 0) return { labels: [], orderData: [], roomData: [] };

    // Group orders by day
    const ordersPerDay: { [key: string]: number } = {};
    const roomsPerDay: { [key: string]: number } = {};

    orders.forEach(order => {
      const date = format(parseISO(order.createdAt), 'yyyy-MM-dd');
      ordersPerDay[date] = (ordersPerDay[date] || 0) + 1;
      
      // Count total rooms for this order
      const roomCount = order.order_rooms ? order.order_rooms.length : 0;
      roomsPerDay[date] = (roomsPerDay[date] || 0) + roomCount;
    });

    // Sort dates and create arrays for chart
    const sortedDates = Object.keys(ordersPerDay).sort();
    const orderCounts = sortedDates.map(date => ordersPerDay[date] || 0);
    const roomCounts = sortedDates.map(date => roomsPerDay[date] || 0);

    return {
      labels: sortedDates.map(date => format(parseISO(date), 'MMM dd')),
      orderData: orderCounts,
      roomData: roomCounts
    };
  };

  const getChartData = () => {
    const { labels, orderData, roomData } = processOrdersPerDay();

    return {
      labels,
      datasets: [
        {
          label: 'Orders per Day',
          data: orderData,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
          fill: true,
          yAxisID: 'y',
        },
        {
          label: 'Rooms Booked per Day',
          data: roomData,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1,
          fill: true,
          yAxisID: 'y1',
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Orders and Rooms Booked Per Day',
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Orders',
        },
        ticks: {
          stepSize: 1,
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Rooms',
        },
        ticks: {
          stepSize: 1,
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      x: {
        title: {
          display: true,
          text: 'Date',
        },
      },
    },
  };

  const getTransportationChartDataForHotel = (hotel: TransportationTotals) => {
    return {
      labels: ['Bus Parking', 'Transit to Venue'],
      datasets: [
        {
          label: 'Number of People',
          data: [hotel.busParking, hotel.transitToVenue],
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(54, 162, 235, 0.7)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

//   const getTransportationChartOptionsForHotel = (hotelName: string) => {
//     return {
//       responsive: true,
//       plugins: {
//         legend: {
//           display: false,
//         },
//         title: {
//           display: true,
//           text: hotelName,
//         },
//       },
//       scales: {
//         y: {
//           beginAtZero: true,
//           title: {
//             display: true,
//             text: 'Number of People',
//           },
//           ticks: {
//             stepSize: 1,
//           },
//         },
//         x: {
//           title: {
//             display: true,
//             text: 'Transportation Type',
//           },
//         },
//       },
//     };
//   };

  return (
    <>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="conference-select-label">Conference</InputLabel>
        <Select
          labelId="conference-select-label"
          id="conference-select"
          value={selectedConference}
          label="Conference"
          onChange={handleConferenceChange}
        >
          {conferences.map((conference) => (
            <MenuItem key={conference.documentId} value={conference.documentId}>
              {conference.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && orders.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
              Orders Report - {orders.length} Total Orders
            </Typography>
            <Box sx={{ height: '400px' }}>
              <Line data={getChartData()} options={chartOptions} />
            </Box>
          </Paper>
        </Box>
      )}
      
      {/* {!loading && transportationData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" component="h3" sx={{ mb: 3, textAlign: 'center' }}>
            Transportation Requirements by Hotel
          </Typography>
          
          {transportationData.map((hotel, index) => (
            <Paper key={hotel.hotelName} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ height: '350px' }}>
                <Bar 
                  data={getTransportationChartDataForHotel(hotel)} 
                  options={getTransportationChartOptionsForHotel(hotel.hotelName)} 
                />
              </Box>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Bus Parking: <strong>{hotel.busParking}</strong> people | 
                  Transit to Venue: <strong>{hotel.transitToVenue}</strong> people
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )} */}
      

      {selectedConference && orders.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
              Transportation Requirements
            </Typography>
            <Typography variant="body1" color="text.secondary">
              No transportation requirements found for orders in the selected conference.
            </Typography>
          </Paper>
        </Box>
      )}

      {!loading && selectedConference && orders.length === 0 && (
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No orders found with 'received' status for the selected conference.
            </Typography>
          </Paper>
        </Box>
      )}
    </>
  );
}